import { wixUpgradeUrl } from '../utils/wixBilling';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ChatModal from './chatModal.js';
import SubscriptionModal from './subscriptionModal.js';
import './floatingChatWidget.css';
import { navigateExternal } from '../utils/navigation.js';
import { canWrite } from '../utils/permissions';
import { disposeWebSocket } from '../utils/disposeWebSocket';

const FLOATING_WIDGET_MENU_ATTR = 'data-floating-widget-menu';

const FloatingChatWidget = () => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [bots, setBots] = useState([]);
  const widgetRef = useRef(null);
  const [selectedBotId, setSelectedBotId] = useState(() => {
    return localStorage.getItem('floatingChatWidgetSelectedBotId') || '';
  });
  const [loading, setLoading] = useState(false);
  const [openChatModal, setOpenChatModal] = useState(false);
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [botToChat, setBotToChat] = useState(null);
  const [botToUpgrade, setBotToUpgrade] = useState(null);

  const fetchBots = async () => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id")}&include_knowledge=False&include_activity=False`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      const completedBots = data.filter((bot) => bot.status === 'completed');
      setBots(completedBots);

      // Validate and update selectedBotId
      const currentSelectedId = localStorage.getItem('floatingChatWidgetSelectedBotId');
      const isValidSelection = completedBots.some(bot => bot.id === currentSelectedId);

      if (completedBots.length > 0) {
        if (!currentSelectedId || !isValidSelection) {
          // If no bot is selected or the selected bot is no longer available, select the first one
          const firstBotId = completedBots[0].id;
          setSelectedBotId(firstBotId);
          localStorage.setItem('floatingChatWidgetSelectedBotId', firstBotId);
        }
      } else {
        // No bots available, clear selection
        setSelectedBotId('');
        localStorage.removeItem('floatingChatWidgetSelectedBotId');
      }
    } catch (err) {
      console.error('Error fetching bots:', err);
    } finally {
      setLoading(false);
    }
  };

  // Don't fetch on mount - only fetch when widget is opened

  // Ensure selectedBotId is always valid when bots change
  useEffect(() => {
    if (bots.length > 0 && selectedBotId) {
      const isValidSelection = bots.some(bot => bot.id === selectedBotId);
      if (!isValidSelection) {
        // Selected bot is no longer available, select the first one
        const firstBotId = bots[0].id;
        setSelectedBotId(firstBotId);
        localStorage.setItem('floatingChatWidgetSelectedBotId', firstBotId);
      }
    } else if (bots.length > 0 && !selectedBotId) {
      // No bot selected but bots are available, select the first one
      const firstBotId = bots[0].id;
      setSelectedBotId(firstBotId);
      localStorage.setItem('floatingChatWidgetSelectedBotId', firstBotId);
    } else if (bots.length === 0 && selectedBotId) {
      // No bots available but something is selected, clear selection
      setSelectedBotId('');
      localStorage.removeItem('floatingChatWidgetSelectedBotId');
    }
  }, [bots, selectedBotId]);

  // WebSocket connection to listen for bot status updates
  useEffect(() => {
    if (!localStorage.getItem("user_id")) return;

    let socket;
    let reconnectTimer = null;
    let disposed = false;
    const connectWebSocket = () => {
      if (disposed) return;
      socket = new WebSocket(`${process.env.REACT_APP_API_WEBSOCKET_URL}`);

      socket.onopen = () => {
        if (disposed) return;
        const userId = localStorage.getItem("user_id");
        socket.send(JSON.stringify({ type: "init", user_id: userId, token: localStorage.getItem('token') }));
      };

      socket.onmessage = (event) => {
        if (disposed) return;
        try {
          const receivedData = JSON.parse(event.data);

          if (receivedData.bot_id && receivedData.status === 'completed') {
            // When a bot becomes completed, refresh the bot list
            fetchBots();
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      socket.onclose = (evt) => {
        if (disposed) return;
        if (evt && evt.code === 4401) {
          console.error('floatingChatWidget WS closed 4401 (auth failure); not reconnecting');
          return;
        }
        reconnectTimer = setTimeout(connectWebSocket, 5000);
      };
    };

    connectWebSocket();
    return () => {
      disposed = true;
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (socket) {
        disposeWebSocket(socket);
      }
    };
  }, []);

  // Fetch bots when widget is expanded
  useEffect(() => {
    if (isExpanded && localStorage.getItem("user_id")) {
      setLoading(true);
      fetchBots();
    }
  }, [isExpanded]);

  // Auto-open chat modal if there's only one bot after loading
  useEffect(() => {
    if (isExpanded && !loading && bots.length === 1 && !openChatModal) {
      const singleBot = bots[0];
      setBotToChat(singleBot);
      setOpenChatModal(true);
      setIsExpanded(false);
    }
  }, [isExpanded, loading, bots, openChatModal]);

  const handleToggleExpand = () => {
    // If there's only one bot, directly open the chat modal
    if (bots.length === 1 && !loading) {
      const singleBot = bots[0];
      setBotToChat(singleBot);
      setOpenChatModal(true);
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const handleClose = () => {
    setIsExpanded(false);
  };

  // Close widget when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isExpanded && widgetRef.current && !widgetRef.current.contains(event.target)) {
        const isInsideSelectMenu = event.target.closest(`[${FLOATING_WIDGET_MENU_ATTR}="true"]`);
        if (isInsideSelectMenu) {
          return;
        }
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  const handleBotChange = (e) => {
    const botId = e.target.value;
    setSelectedBotId(botId);
    localStorage.setItem('floatingChatWidgetSelectedBotId', botId);
    // Do NOT auto-open the chat modal or collapse the widget here;
    // user will explicitly click "Test" to open the modal.
  };

  const handleTestClick = () => {
    const selectedBot = bots.find(bot => bot.id === selectedBotId);
    if (selectedBot) {
      setBotToChat(selectedBot);
      setOpenChatModal(true);
      setIsExpanded(false);
    }
  };

  const handleUpgradeClick = async (bot) => {
    // Read-only teammates aren't allowed to start/modify a subscription.
    // Surface the same unified "permission denied" modal used everywhere
    // else in the app (see useUpgrade.js / PermissionToast.js).
    if (!canWrite()) {
      window.dispatchEvent(
        new CustomEvent('ub:permission_denied', { detail: { message: '' } })
      );
      return;
    }
    setOpenChatModal(false);
    const BASE = process.env.REACT_APP_API_BASE_URL;

    // Check if bot has an active/trialing Stripe subscription - prioritize Stripe over Wix
    const hasActiveStripeSubscription = bot?.subscription?.stripe_customer_id &&
      (bot?.subscription?.status === 'active' || bot?.subscription?.status === 'trialing');

    if ((bot?.wix_instance_id || bot?.wix_product) && !hasActiveStripeSubscription) {

      try {
        const res = await fetch(`${BASE}wix/status?bot_id=${encodeURIComponent(bot.id)}`);
        const data = await res.json();

        if (data?.instance_id) {
          if (bot?.subscription_plan?.name === 'Free') {
            if (window.dataLayer) {
              window.dataLayer.push({ event: 'checkout_session_started' });
            }
          }
          navigateExternal(wixUpgradeUrl(data));
          return;
        }
      } catch (err) {
        console.error('Wix status check failed – falling back to Stripe:', err);
      }
    }

    // Internal subscriptions or Free plan → open modal instead of Stripe portal
    const isInternalSubscription = bot?.subscription?.stripe_subscription_id === 'internal';

    if (bot?.subscription_plan?.name === 'Free' || isInternalSubscription) {
      setBotToUpgrade(bot);
      setOpenSubscriptionModal(true);
    } else {
      try {
        const res = await fetch(`${BASE}create-customer-portal-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            stripe_customer_id: bot.subscription.stripe_customer_id,
          }),
        });

        const data = await res.json();

        if (data?.url) {
          navigateExternal(data.url);
        } else {
          alert(t('fcw_portal_session_failed'));
        }
      } catch (err) {
        console.error('Failed to open customer portal:', err);
        alert(t('something_went_wrong'));
      }
    }
  };

  const handleChatModalClosed = async () => {
    setOpenChatModal(false);
    // Refresh bots to get updated message counts
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id")}&include_knowledge=False&include_activity=False`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const completedBots = data.filter((bot) => bot.status === 'completed');
        setBots(completedBots);

        // Validate selectedBotId after refresh
        const currentSelectedId = selectedBotId;
        const isValidSelection = completedBots.some(bot => bot.id === currentSelectedId);

        if (completedBots.length > 0) {
          if (!currentSelectedId || !isValidSelection) {
            const firstBotId = completedBots[0].id;
            setSelectedBotId(firstBotId);
            localStorage.setItem('floatingChatWidgetSelectedBotId', firstBotId);
          }
        } else {
          setSelectedBotId('');
          localStorage.removeItem('floatingChatWidgetSelectedBotId');
        }
      }
    } catch (err) {
      console.error('Error refreshing bots:', err);
    }
  };

  const handleSubscriptionChange = async () => {
    setOpenSubscriptionModal(false);
    // Refresh bots after subscription change
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id")}&include_knowledge=False&include_activity=False`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const completedBots = data.filter((bot) => bot.status === 'completed');
        setBots(completedBots);

        // Validate selectedBotId after refresh
        const currentSelectedId = selectedBotId;
        const isValidSelection = completedBots.some(bot => bot.id === currentSelectedId);

        if (completedBots.length > 0) {
          if (!currentSelectedId || !isValidSelection) {
            const firstBotId = completedBots[0].id;
            setSelectedBotId(firstBotId);
            localStorage.setItem('floatingChatWidgetSelectedBotId', firstBotId);
          }
        } else {
          setSelectedBotId('');
          localStorage.removeItem('floatingChatWidgetSelectedBotId');
        }
      }
    } catch (err) {
      console.error('Error refreshing bots:', err);
    }
  };

  if (!localStorage.getItem("user_id")) {
    return null;
  }

  const validSelectedBotId = bots.some(bot => bot.id === selectedBotId) ? selectedBotId : '';

  return (
    <>
      {isExpanded && <div className="floating-widget-overlay" />}
      <div
        ref={widgetRef}
        className={`floating-chat-widget ${isExpanded ? 'expanded' : ''}`}
      >
        {!isExpanded ? (
          <div className="floating-chat-widget-collapsed" onClick={handleToggleExpand}>
            <FontAwesomeIcon icon={faComments} className="collapsed-icon" />
            <div className="vertical-text">{t('header_test_agent')}</div>
          </div>
        ) : (
          <div className="floating-chat-widget-expanded">
            <div className="widget-header">
              <span>{t('header_test_agent')}</span>
              <button className="close-widget-btn" onClick={handleClose}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="widget-content">
              {loading ? (
                <div className="widget-loading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>{t('fcw_loading_bots')}</span>
                </div>
              ) : bots.length === 0 ? (
                <div className="widget-empty">{t('fcw_no_completed_bots')}</div>
              ) : (
                <>
                  <div className="widget-select-container">
                    <FormControl
                      sx={{
                        width: '100%',
                        '& label.Mui-focused': {
                          color: '#5616ea',
                        },
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            border: "1px solid #515151",
                          },
                        },
                      }}
                    >
                      <InputLabel id="widget-bot-select-label">{t('select_bot')}</InputLabel>
                      <Select
                        labelId="widget-bot-select-label"
                        value={validSelectedBotId}
                        onChange={handleBotChange}
                        label={t('select_bot')}
                        className="widget-bot-select"
                        MenuProps={{
                          PaperProps: {
                            [FLOATING_WIDGET_MENU_ATTR]: 'true',
                          },
                        }}
                      >
                        {bots.map((bot) => (
                          <MenuItem
                            key={bot.id}
                            value={bot.id}
                            className="widget-bot-select-item"
                          >
                            {bot.name || bot.domain_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <button
                    className="widget-test-btn"
                    onClick={handleTestClick}
                    disabled={!validSelectedBotId}
                  >
                    <FontAwesomeIcon icon={faComments} />
                    {t('skill_chat_test_tab')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {openChatModal && botToChat && (
        <ChatModal
          open={openChatModal}
          onClose={handleChatModalClosed}
          bot={botToChat}
          currentMessageCount={botToChat.current_message_count ?? 0}
          messagePlanLimit={botToChat.subscription_plan?.message_limit ?? 999999}
          planName={botToChat.subscription_plan?.name ?? 'Free'}
          openSubscriptionModalFromChat={handleUpgradeClick}
        />
      )}

      {openSubscriptionModal && botToUpgrade && (
        <SubscriptionModal
          open={openSubscriptionModal}
          bot={botToUpgrade}
          onClose={handleSubscriptionChange}
        />
      )}
    </>
  );
};

export default FloatingChatWidget;
